import { useEffect, useState } from "react";
import {
    MessageSquare, Send, Loader2, Eye, Link2, Copy, CheckCircle2, ExternalLink,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleString("it-IT", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    }) : "—";

export function QuoteCommentsThread({ quoteId, currentUser }) {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/quotes/${quoteId}/comments`);
            setComments(data || []);
        } catch (e) {
            // silent on 403 (non-owner)
            if (e?.response?.status !== 403) console.error("Load comments failed:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [quoteId]);

    const send = async () => {
        const txt = body.trim();
        if (!txt) return;
        setSending(true);
        try {
            const { data } = await api.post(`/quotes/${quoteId}/comments`, { body: txt });
            setComments((c) => [...c, data]);
            setBody("");
            toast.success("Messaggio inviato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore invio messaggio");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="card p-6 sm:p-8" data-testid="quote-comments-section">
            <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#0C4650]" />
                <h3 className="text-lg font-black text-[#0C4650]">
                    Conversazione con la sede
                </h3>
                {comments.length > 0 && (
                    <span className="ml-auto text-xs font-bold text-[#898A8D]">
                        {comments.length} messaggi
                    </span>
                )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
                Scrivi note, chiarimenti o richieste. Verrà inviata anche un'email
                di notifica alla controparte.
            </p>

            <div className="mt-5 space-y-3 max-h-[420px] overflow-y-auto pr-1"
                 data-testid="quote-comments-list">
                {loading ? (
                    <div className="text-center py-8 text-gray-400">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-400">
                        Nessun messaggio. Inizia la conversazione qui sotto.
                    </div>
                ) : (
                    comments.map((c) => {
                        const mine = currentUser && c.author_id === currentUser.id;
                        const isAdmin = c.author_role === "admin";
                        return (
                            <div key={c.id}
                                 className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                 data-testid={`comment-${c.id}`}>
                                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                                    mine
                                        ? "bg-[#0C4650] text-white"
                                        : isAdmin
                                            ? "bg-[#C7FF2C]/30 border-2 border-[#C7FF2C] text-[#0C4650]"
                                            : "bg-[#f7f7f5] text-[#0C4650]"
                                }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] tracking-widest font-black ${
                                            mine ? "text-[#C7FF2C]" : "text-[#898A8D]"
                                        }`}>
                                            {isAdmin ? "SEDE" : "AGENTE"} · {c.author_name || "—"}
                                        </span>
                                    </div>
                                    <div className="whitespace-pre-line text-sm">{c.body}</div>
                                    <div className={`text-[10px] mt-1.5 ${
                                        mine ? "text-[#91C6C0]" : "text-gray-400"
                                    }`}>
                                        {fmtDate(c.created_at)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="mt-4 flex gap-2 items-end">
                <textarea
                    rows={2}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
                    }}
                    placeholder="Scrivi un messaggio… (Ctrl/Cmd + Invio per inviare)"
                    data-testid="comment-input"
                    className="flex-1 rounded-xl border-2 border-gray-200 p-3 text-sm focus:border-[#0C4650] outline-none resize-none"
                />
                <button
                    onClick={send}
                    disabled={sending || !body.trim()}
                    className="btn-primary disabled:opacity-50 self-end"
                    data-testid="comment-send"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Invia
                </button>
            </div>
        </div>
    );
}

export function QuoteTrackingPanel({ quoteId }) {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/quotes/${quoteId}/tracking`);
            setInfo(data);
        } catch (e) {
            // silent on 403 (non-owner)
            if (e?.response?.status !== 403) console.error("Load tracking failed:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [quoteId]);

    if (loading) {
        return (
            <div className="card p-6 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
        );
    }
    if (!info?.tracking_token) return null; // legacy quote senza token

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(info.public_url);
            setCopied(true);
            toast.success("Link copiato");
            setTimeout(() => setCopied(false), 1500);
        } catch (_) {
            toast.error("Impossibile copiare");
        }
    };

    return (
        <div className="card p-6 sm:p-8" data-testid="quote-tracking-section">
            <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#0C4650]" />
                <h3 className="text-lg font-black text-[#0C4650]">Tracciamento PDF</h3>
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] tracking-widest font-bold text-[#0C4650] bg-[#C7FF2C]/40 px-2 py-1 rounded-full">
                    NEW
                </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
                Sai esattamente quando il cliente apre la proposta e quando la conferma online.
            </p>

            <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <Tile
                    label="Aperture"
                    value={info.visits_count || 0}
                    accent={info.visits_count > 0}
                    icon={<Eye className="w-4 h-4 text-[#0C4650]" />}
                />
                <Tile
                    label="Ultima apertura"
                    value={fmtDate(info.last_visit_at)}
                />
                <Tile
                    label="Conferma online"
                    value={info.client_confirmed_at ? "✓ Confermato" : "In attesa"}
                    accent={!!info.client_confirmed_at}
                    icon={info.client_confirmed_at ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : null}
                />
            </div>

            {info.client_confirm_note && (
                <div className="mt-3 rounded-xl bg-emerald-50 border-2 border-emerald-200 p-3 text-sm text-emerald-800"
                     data-testid="confirm-note-display">
                    <div className="text-[10px] tracking-widest font-bold text-emerald-700">NOTA DEL CLIENTE</div>
                    <div className="mt-1 italic">"{info.client_confirm_note}"</div>
                </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <div className="flex-1 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[#f7f7f5] text-sm text-[#0C4650] font-mono truncate">
                    <Link2 className="w-4 h-4 shrink-0" />
                    <span className="truncate" data-testid="tracking-public-url">
                        {info.public_url}
                    </span>
                </div>
                <button
                    onClick={copyLink}
                    className="btn-outline text-sm"
                    data-testid="tracking-copy-link"
                >
                    {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copiato!" : "Copia link"}
                </button>
                <a
                    href={info.public_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost text-sm"
                    data-testid="tracking-open-link"
                >
                    <ExternalLink className="w-4 h-4" /> Apri
                </a>
            </div>

            {info.visits && info.visits.length > 0 && (
                <details className="mt-5">
                    <summary className="text-xs font-bold text-[#0C4650] cursor-pointer">
                        Cronologia apertura ({info.visits.length} eventi)
                    </summary>
                    <div className="mt-3 space-y-1 text-xs text-gray-600">
                        {info.visits.map((v) => (
                            <div key={v.id} className="flex justify-between gap-2 border-b border-gray-100 py-1">
                                <span>
                                    {v.kind === "confirm" ? "✓ Conferma" : "👁 Apertura"}
                                </span>
                                <span className="text-gray-400">{fmtDate(v.visited_at)}</span>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

const Tile = ({ label, value, accent, icon }) => (
    <div className={`rounded-xl p-3 border-2 ${
        accent ? "bg-[#C7FF2C]/30 border-[#C7FF2C]" : "bg-[#f7f7f5] border-transparent"
    }`}>
        <div className="text-[10px] tracking-widest font-bold text-[#898A8D] inline-flex items-center gap-1">
            {icon}{label.toUpperCase()}
        </div>
        <div className="font-black text-base text-[#0C4650] mt-1">{value}</div>
    </div>
);
